/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

/**
 * AngularJS component that handles rendering all the groups within the groups tab in the header.
 */
const groupHeaderComponent = {
  templateUrl: 'ajs/components/group/group-header.html',
  bindings: {
    initializeTrigger: '@',
  },
  controllerAs: 'groupHeaderCtrl',
  controller: ['$attrs', '$rootScope', '$scope', '$state', '$translate', '$window', 'Restangular',
    'AeroDialogService', 'BulbAuthService', 'BulbBrowserService', 'BulbBusyService',
    'BulbGoogleClassroomImportService', 'BulbGroupService', 'BulbModalService',
    'BulbUtilitiesService', 'AUTH', 'GROUP',
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    GroupHeaderController],
};

/**
 * Controller ( Class ) for the groupHeaderComponent definition object.
 */
export function GroupHeaderController(
  $attrs,
  $rootScope,
  $scope,
  $state,
  $translate,
  $window,
  Restangular,
  AeroDialogService,
  BulbAuthService,
  BulbBrowserService,
  BulbBusyService,
  BulbGoogleClassroomImportService,
  BulbGroupService,
  BulbModalService,
  BulbUtilitiesService,
  AUTH,
) {
  const that = this;
  const { unit } = this;
  const hasOrgMembership = BulbAuthService.currentUser.groupMemberships
    .findIndex((index) => index.type === 'ORGANIZATION');
  this.isOwner = BulbAuthService.currentUser.username === unit?.ownerUsername;
  this.isOrgStudent = BulbAuthService.currentUser.position === 'STUDENT' && hasOrgMembership !== -1;
  this.allowed = BulbAuthService.currentUser.allowed;
  /**
   * Called on each controller after all the controllers on an element have been constructed and had their bindings
   * initialized (and before the pre & post linking functions for the directives on this element). This is a good
   * place to put initialization code for your controller.
   */
  this.$onInit = function onInit() {
    $scope.CONFIG = $rootScope.CONFIG;
    // Fetch initial groups upon component creation to be displayed in groups tab
    this.getInitialGroups();

    if (unit?.apiCreated) {
      this.startDate = unit.startDate ? BulbUtilitiesService
        .convertDateWithTranslatedMonth(unit.startDate) : '';
      this.endDate = unit.endDate ? BulbUtilitiesService.convertDateWithTranslatedMonth(unit.endDate) : '';
      this.isOverridden = unit.overrideAutoDates;
      this.effectiveDatesIcon = this.isOverridden ? 'exclamation-triangle' : 'calendar-alt';
    }
    if (unit?.lastDateClosed) {
      this.groupClosedDate = BulbUtilitiesService.convertDateWithTranslatedMonth(unit.lastDateClosed);
    }
  };

  /**
   * Called after this controller's element and its children have been linked. Similar to the post-link function
   * this hook can be used to set up DOM event handlers and do direct DOM manipulation.
   */
  this.$postLink = function postLink() {
    if ('initializeTrigger' in $attrs) {
      // Wait to make the first server request until initializeTrigger evaluates to true
      const unobserve = $attrs.$observe('initializeTrigger', (value) => {
        if ($scope.$eval(value)) {
          that.getInitialGroups();
          unobserve();
        }
      });
    }
  };

  // Check to see if the current user can create MacMillan Groups:
  const { codes } = BulbAuthService.currentUser.features.ActivatedCodesFeature;
  this.ableToCreateMacMillanGroups = BulbAuthService.isAbleToMakeMacMillanGroups(codes);

  // Establish whether or not the group is a content group, and see if the current user is
  // the owner of the group.
  this.isOwnerOfGroup = function isOwnerOfGroup(group) {
    return BulbAuthService.currentUser.hasPermission(group, AUTH.PERMISSIONS.DELETE);
  };

  /**
   * All retrieved groups that the account is a part of, and the filtered groups used to display.
   * @type {groups|*|groups.groups|Array|$scope.groups}
   */
  this.groups = BulbGroupService.groups;
  this.currentDisplayedGroupStatus = BulbGroupService.currentStatus;
  this.enteredGroupCode = '';
  this.getRemainingGroups = BulbGroupService.getRemainingGroups();
  this.fieldErrors = {};
  this.isOrgMember = BulbGroupService.accountIsInOrg();

  this.isEducator = BulbAuthService.currentUser.position === 'EDUCATOR';

  /**
   * Check if user has required billing or membership and isEducator and isInOrg to import class from Google Classroom or not .
   * @returns {boolean} true or false
   */
  this.canImportClassFromGoogleClassroom = function canImportClassFromGoogleClassroom() {
    return (BulbAuthService.currentUser.type !== 'FREE' && that.isEducator && that.isOrgMember);
  };

  /**
   * Function to test whether the user has the codes features enabled.
   */
  this.getBookCodes = function getBookCodes() {
    const activatedCodesFeature = BulbAuthService.currentUser.features.ActivatedCodesFeature;
    if (BulbAuthService.currentUser.position === 'EDUCATOR' && activatedCodesFeature) {
      const { codes } = activatedCodesFeature;
      if (codes.length > 0) {
        this.isMacMillanTeacher = true;
      }
    }
  };
  /**
   * Grab the potential codes from the user.
   */
  this.getBookCodes();

  // Check to find out if its a mobile device so that we hide the import from Google Classroom button. But we do
  // want to have it enabled for devices such as iPad.
  this.isMobile = BulbBrowserService.isIOSMobile() || BulbBrowserService.isAndroid()
    || BulbBrowserService.isWindowsMobile();

  /**
   * Requests this first page of groups
   */
  this.getInitialGroups = function getInitialGroups(calledFromGroupContextSwitch) {
    const authenticated = BulbAuthService.currentUser.isAuthenticated();

    if (authenticated && (calledFromGroupContextSwitch || (!this.groups || this.groups.length === 0))) {
      BulbGroupService.fetchGroups(null, $rootScope.GROUP.PAGE_SIZE, that.currentDisplayedGroupStatus);
    }
  };

  /**
   * Requests the next page of groups based on the last group's
   * sort key.
   */
  this.getNextPage = function getNextPage() {
    if (!BulbGroupService.fetchedAllGroupsForToggle) {
      const initialGroupsLength = this.groups.length;
      if (initialGroupsLength === 0) {
        this.getInitialGroups();
      } else {
        const previousGroup = this.groups[initialGroupsLength - 1];

        BulbGroupService.fetchGroups(previousGroup.sortKey, $rootScope.GROUP.PAGE_SIZE, that.currentDisplayedGroupStatus);
      }
    }
  };

  /**
   * Sends the create request with the this.group model.
   */
  this.createGroup = function createGroup() {
    // clear old field errors
    this.fieldErrors = {};
    BulbBusyService.showBusyDuring(
      BulbGroupService.createGroup(this.group).then((group) => {
        // navigate to group page, with animations
        $state.go('group', { groupUrl: group.url }, { location: 'replace' });
      }, (error) => {
        // DO LATER : convert this to use the new form validation handler to update the field errors.
        if (error.data && error.data.errorMap) {
          const { fieldErrors } = error.data.errorMap;
          if (fieldErrors) {
            that.fieldErrors = fieldErrors;
          }
        }
      }),
    );
  };

  /**
   * This function will show the modal, and then join the member with the code they enter.
   */
  this.joinGroupFromCode = function joinGroupFromCode(codeJustFailed, failedCode, isGroupExists, groupName) {
    const joinGroupModalOptions = {
      titleCode: 'join_group_modal_title',
      templateUrl: 'joingroupmodal.nested',
      fontAwesomeCode: 'fas fa-users',
      showOkButton: false,
      showCancelButton: false,
      windowClass: 'bulb-aero-modal-body bulb-aero-modal-buttons group-join-specific-modal-designs',
      controller: ['$scope', function joinGroupModalController($scope) {
        if (codeJustFailed) {
          $scope.enteredGroupCode = failedCode;
          $scope.codeJustFailed = true;
          // Check the group with given code already exists or not
          if (isGroupExists) {
            $scope.isGroupExists = true;
            $scope.groupName = groupName;
          }
          // Watch for a change in the enteredGroupCode variable. If it changes, we need to remove
          // all the warnings.
          $scope.inputChanged = function joinGroupModalInputChanged() {
            $scope.codeJustFailed = false;
            $scope.isGroupExists = false;
          };
        }
      }],
    };

    BulbModalService.show(joinGroupModalOptions).then((groupCode) => {
      groupCode = groupCode.toUpperCase();

      BulbBusyService.showBusyDuring(
        Restangular.all(`token/member/${groupCode}`).post({}).then(() => {
          // We have successfully joined the group at this point, now we need to fetch the group
          // using the token.
          Restangular.one(`groups/${groupCode}`).get().then((fetchedGroup) => {
            // Show the toast that it was successful and you've been added to the group.
            const messageCode = 'joined_group_text';

            // @ts-ignore
            iziToast.show({ // jshint ignore:line
              theme: 'dark',
              timeout: 2000,
              close: false,
              icon: 'fas fa-users',
              message: $translate.instant(messageCode, { group: fetchedGroup.name }),
              position: 'center',
              progressBarColor: '#00AEEF', // $aero-blue
              onClosed() {
                $state.go('group', { groupUrl: fetchedGroup.url });
              },
            });
            BulbGroupService.joinedGroup(fetchedGroup);
          });
        }, (error) => {
          // Check the group with given code already exists or not
          let isGroupExists = false;
          let groupName;
          const message = error.data.developerMessage;
          if (error.status === 400 && message
            && message.indexOf('groupName:') > 0) {
            isGroupExists = true;
            groupName = message.substring(message.lastIndexOf(':') + 1);
          }
          // If it fails, show it fails with the other code or already exists with the given code.
          that.joinGroupFromCode(true, groupCode, isGroupExists, groupName);
        }),
      );
    });
  };

  /**
   * Changes the state to the create group state if can create more groups.
   */
  this.navigateCreateGroup = function navigateCreateGroup() {
    let modalOptions = {};

    // Since it is possible to be a free user in a group, we need to make sure that if they are free, they are not
    // part of an org and if they are part of an org we let them create a group.
    if (BulbAuthService.currentUser.orgSuspended) {
      AeroDialogService.showOrgSuspended();
    } else if (!BulbAuthService.currentUser.canCreate('Group')) {
      modalOptions = {
        titleCode: 'group_upgrade_from_free_tier_title',
        templateUrl: 'groupupgrade.nested',
        showOkButton: true,
        showCancelButton: true,
      };
      BulbModalService.show(modalOptions);
    } else if (BulbGroupService.getRemainingGroups() >= 1) {
      $state.go('createGroup');
    } else {
      modalOptions = {
        titleCode: 'group_limit_reached_title',
        templateUrl: 'grouplimitreached.nested',
        showOkButton: false,
        showCancelButton: false,
      };
      BulbModalService.show(modalOptions);
    }
  };

  /**
   * Back function for the Cancel button.
   */
  this.back = function back() {
    $window.history.back();
  };

  /**
   * A function to sort the user's entire group list.
   */
  this.changeGroupListFilter = function changeGroupListFilter(newGroupStatus) {
    BulbGroupService.fetchedAllGroupsForToggle = false;
    that.currentDisplayedGroupStatus = newGroupStatus;

    if (newGroupStatus !== BulbGroupService.currentStatus) {
      BulbGroupService.currentStatus = newGroupStatus;
      BulbGroupService.groupListWasToggled = true;
    }

    that.getInitialGroups(true);
  };

  /**
   * Function that will show a modal if a user is attempting to close their group. This will ensure that
   * a user does not accidentally close their group.
   */
  this.showCloseGroupModal = function showCloseGroupModal(event, group) {
    // Stop any click events after showing this modal.
    event.stopPropagation();
    event.stopImmediatePropagation();
    BulbGroupService.closeGroupModalOptions.onPrimary = () => {
      that.toggleGroupStatus(event, group);
    };
    AeroDialogService.showDialog(BulbGroupService.closeGroupModalOptions);
  };

  /**
   * This is a helper function to call the GroupService function that will toggle a group active or not.
   */
  this.toggleGroupStatus = function toggleGroupStatus(event, group) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // Only the owner of the group can toggle whether or not it's active.
    if (this.isOwnerOfGroup(group)) {
      BulbGroupService.toggleGroupStatus(group).then((response) => {
        const messageCode = (response.updatedGroup.active) ? 'group_toast_group_opened' : 'group_toast_group_closed';

        // @ts-ignore
        iziToast.show({ // jshint ignore:line
          theme: 'dark',
          timeout: 2000,
          close: false,
          icon: 'fas fa-inbox',
          message: $translate.instant(messageCode),
          position: 'bottomRight',
          progressBarColor: '#00AEEF', // $aero-blue
        });
      });
    }
  };

  /**
   * Helper function that calls the GroupService to get the date that the group was closed.
   * @param group the group we're checking.
   * @returns the date that the group was closed, or null.
   */
  this.getDateGroupWasClosed = function getDateGroupWasClosed(group) {
    return BulbGroupService.getDateGroupWasClosed(group);
  };

  // Keep the groups updated.
  $scope.$watchCollection(() => BulbGroupService.groups, (newVal) => {
    that.groups = newVal;
  });

  /**
   * Import classes from google classroom .
   */
  this.importFromClassroom = function importFromClassroom() {
    BulbGoogleClassroomImportService.importGroupFromClassroom();
  };

  /**
   * Function that displays the MacMillan 3 Book Dialog. It uses a downgraded Angular8 Service.
   */
  this.openBookDialog = function openBookDialog() {
    AeroDialogService.showMacMillanBookDialog(true, true);
  };
}

export default groupHeaderComponent;
